import { createFileRoute } from '@tanstack/react-router'
import React, { useEffect, useMemo, useRef, useDeferredValue } from 'react'
import { VarianceChart } from '../components/charts/VarianceChart'
import { CurrentVarianceTable } from '../components/tables/CurrentVarianceTable'
import { VarianceHistoryTable } from '../components/tables/VarianceHistoryTable'
import FullPageLoader from '../components/ui/FullPageLoader'
import { CONFIG } from '../config/constants'
import { useAnalysisHistory } from '../hooks/useAnalysisHistory'
import { useDashboardData } from '../hooks/useDashboardData'
import { usePageTitle } from '../hooks/usePageTitle'
import { useFastMount } from '../hooks/useFastMount'
import { useSettingsStore } from '../stores/settingsStore'

export const Route = createFileRoute('/_auth/variance')({
    component: VarianceRoute,
})

function VarianceRoute() {
    usePageTitle('Variance Analysis')
    // FREEZE FIX v3: Granular selectors prevent re-renders from unrelated store changes
    const symbol = useSettingsStore(s => s.symbol)
    const expiry = useSettingsStore(s => s.expiry)
    const mode = useSettingsStore(s => s.mode)
    const range = useSettingsStore(s => s.range)
    const baseTime = useSettingsStore(s => s.baseTime)
    const rowsPerPage = useSettingsStore(s => s.rowsPerPage)
    const setRowsPerPage = useSettingsStore(s => s.setRowsPerPage)
    const setExpiry = useSettingsStore(s => s.setExpiry)

    // FREEZE FIX: Defer the parameters passed to heavy sub-components
    const deferredSymbol = useDeferredValue(symbol)
    const deferredExpiry = useDeferredValue(expiry)
    const deferredMode = useDeferredValue(mode)
    const deferredRange = useDeferredValue(range)

    // 1. Live Data
    const { marketData, analysis, isLoading: isMarketLoading, effectiveExpiry } = useDashboardData()

    // 🚀 PERF: Defer heavy data so live ticks don't block user interactions
    const deferredMarketData = useDeferredValue(marketData)
    const deferredAnalysis = useDeferredValue(analysis)

    // FREEZE FIX v3: Ref-based guard prevents circular setExpiry loop
    const expiryRef = useRef(expiry)
    expiryRef.current = expiry
    useEffect(() => {
        if (effectiveExpiry && effectiveExpiry !== expiryRef.current) {
            setExpiry(effectiveExpiry)
        }
    }, [effectiveExpiry, setExpiry])

    // 2. History Data
    const historyData = useAnalysisHistory({
        symbol: deferredSymbol,
        expiry: deferredExpiry || effectiveExpiry || '',
        range: deferredRange,
        mode: deferredMode,
        baseTimePattern: baseTime,
        viewType: 'default',
    })

    // 3. Construct Merged Data for Chart
    // Combine History + Current Analysis Point
    const mergedData = useMemo(() => {
        const hist = historyData.data || []
        let rawData: any[] = []

        // If we have live analysis, create a live data point
        if (analysis && analysis.variance && marketData) {
            const livePoint = {
                timestamp: new Date(marketData.timestamp || Date.now()).toISOString(),

                maxDiff: analysis.variance.maxDiff || 0,
                maxStrike: analysis.variance.maxStrike || analysis.resistanceStrike,
                minStrike: analysis.variance.minStrike || analysis.supportStrike,
                minDiff: analysis.variance.minDiff || 0,
                finalVariance: analysis.variance.finalVariance || 0,

                ceCount: analysis.variance.ceCount || 0,
                peCount: analysis.variance.peCount || 0,
                totalCount: analysis.variance.totalCount || 0,

                shift: analysis.shift,
                atmStrike: analysis.atmStrike,
            }
            // Append to history
            rawData = [...hist, livePoint]
        } else {
            rawData = [...hist]
        }

        // Filtering Logic: REMOVED as per user request ("Not loading full history")
        // Previously filtered if variance diff > 0.01.
        // Now showing all available data points (sorted descending).

        // Sort Ascending first (to ensure correct time order before reversing)
        const sortedAsc = [...rawData].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

        return sortedAsc.reverse()
    }, [historyData.data, analysis, marketData])



    const isMounted = useFastMount()

    if (!isMounted || (isMarketLoading && !marketData && (!historyData.data || historyData.data.length === 0))) {
        return (
            <div className="space-y-6 pb-20 p-4">
                <div className="min-h-[450px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 animate-pulse border border-slate-200/50 dark:border-slate-700/50" />
                <div className="min-h-[380px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 animate-pulse border border-slate-200/50 dark:border-slate-700/50" />
            </div>
        )
    }

    return (
        <div className="space-y-6 pb-20 animate-in fade-in duration-500">
                {/* 1. Variance Chart */}
                {mergedData.length > 0 && (
                    <VarianceChart
                        data={mergedData}
                        symbol={deferredSymbol}
                        isLoading={isMarketLoading}
                    />
                )}

                {/* 2. Current Variance Analysis */}
                <CurrentVarianceTable
                    analysis={deferredAnalysis}
                    marketData={deferredMarketData}
                    mode={deferredMode as any}
                    isLive={true}
                    historyData={historyData.data}
                />

                {/* 3. Variance History */}
                <VarianceHistoryTable
                    data={historyData.data}
                    symbol={deferredSymbol}
                    mode={deferredMode}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={setRowsPerPage}
                    isLoading={isMarketLoading}
                />
        </div>
    )
}
